// Billing-page slice: MVP total = sum of agent balances (platform-scope
// credits are not issued in v1 — see spec §4 scope note; upgrade path is a
// GET /me/credit-balance proxy route), card block, subscriptions, and a
// lazily-fetched paginated ledger.

import { useCallback, useMemo, useRef, useState } from 'react';
import { usePaymentsProfile, withTokenRetry } from './usePaymentsProfile';
import { getCreditLedger, type LedgerEntry, type PaymentsSubscription } from '@/src/marketplace/payments';
import { useConfig } from '@/src/config/context';
import { NANOCREDITS_PER_CREDIT } from '@/src/lib/credits';

const PAGE_SIZE = 20;

export interface BillingState {
  status: 'disabled' | 'loading' | 'ready' | 'unavailable';
  totalCredits: number | null;
  card: { brand: string; last4: string; expMonth: number | null; expYear: number | null } | null;
  subscriptions: PaymentsSubscription[];
  ledger: {
    entries: LedgerEntry[];
    total: number | null;
    state: 'idle' | 'loading' | 'ready' | 'error';
    loadMore: () => void;
  };
  refetch: () => void;
}

export function useBilling(): BillingState {
  const { status, profile, refetch, getToken } = usePaymentsProfile();
  const config = useConfig();

  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [ledgerState, setLedgerState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  // ponytail: offset pagination over a newest-first mutable ledger — a new
  // entry between pages shifts offsets and can duplicate rows; cursor
  // pagination if it matters.
  const offsetRef = useRef(0);
  const inflightRef = useRef(false);

  const loadMore = useCallback(() => {
    if (inflightRef.current || ledgerState === 'loading' || status !== 'ready') return;
    inflightRef.current = true;
    setLedgerState('loading');
    withTokenRetry(getToken, (token) => getCreditLedger(config.marketplaceBaseUrl, token, PAGE_SIZE, offsetRef.current))
      .then((page) => {
        offsetRef.current += page.entries.length;
        setEntries((prev) => [...prev, ...page.entries]);
        setTotal(page.total);
        setLedgerState('ready');
        inflightRef.current = false;
      })
      .catch((err: unknown) => {
        // Same fix as the provider's refetch: keep the failure reason visible
        // instead of swallowing it.
        console.error('credit ledger fetch failed', err);
        setLedgerState('error');
        inflightRef.current = false;
      });
  }, [config.marketplaceBaseUrl, getToken, ledgerState, status]);

  return useMemo(() => {
    const card = profile?.card?.brand && profile.card.last4
      ? { brand: profile.card.brand, last4: profile.card.last4, expMonth: profile.card.expMonth, expYear: profile.card.expYear }
      : null;
    return {
      status,
      totalCredits: profile ? profile.agentCredits.reduce((sum, c) => sum + c.creditsAvailableNc, 0) / NANOCREDITS_PER_CREDIT : null,
      card,
      subscriptions: profile?.subscriptions ?? [],
      ledger: { entries, total, state: ledgerState, loadMore },
      refetch,
    };
  }, [status, profile, entries, total, ledgerState, loadMore, refetch]);
}
